import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { EquipmentType, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface EquipmentTypeStore {
  equipmentTypes: Ref<Loadable<EquipmentType[]>>;
  loadEquipmentTypes: (opts?: PerformLoadOptions) => Promise<void>;
  loadUsedEquipmentTypes: (opts?: PerformLoadOptions) => Promise<void>;
  usedEquipmentTypes: Ref<Loadable<EquipmentType[]>>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useEquipmentTypeStore = defineStore(
  'equipmentType',
  (): EquipmentTypeStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const toast = useToast();

    const equipmentTypes: Ref<Loadable<EquipmentType[]>> = ref({
      loading: false,
    });

    const usedEquipmentTypes: Ref<Loadable<EquipmentType[]>> = ref({
      loading: false,
    });

    const loadEquipmentTypes = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const numEquipmentTypes = equipmentTypes.value.data?.length ?? 0;
      const isLoading = equipmentTypes.value.loading;
      const requiresLoad = numEquipmentTypes === 0 && !isLoading;

      if (options.bypassCache || requiresLoad) {
        return performLoad();
      }
    };

    const performLoad = async () => {
      if (!authorization.hasAuthority('client', 'read')) {
        return;
      }

      try {
        equipmentTypes.value.loading = true;
        equipmentTypes.value.data = await api.equipmentType.findAllTypes();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        equipmentTypes.value.error = e;
        console.error('Error loading equipment types', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('carriers.equipmentTypes', 2),
          }),
          error: e,
        });
      } finally {
        equipmentTypes.value.loading = false;
      }
    };

    const loadUsedEquipmentTypes = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const isEmpty = !usedEquipmentTypes.value.data?.length;
      const requiresLoad =
        options.bypassCache || (isEmpty && !usedEquipmentTypes.value.loading);

      if (!requiresLoad || !authorization.hasAuthority('client', 'read')) {
        return;
      }

      try {
        usedEquipmentTypes.value.loading = true;
        usedEquipmentTypes.value.data =
          await api.equipmentType.findUsedTypes();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        usedEquipmentTypes.value.error = e;
        console.error('Error loading used equipment types', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('carriers.equipmentTypes', 2),
          }),
          error: e,
        });
      } finally {
        usedEquipmentTypes.value.loading = false;
      }
    };

    watch(selectedDomainId, () => {
      equipmentTypes.value.data = [];
      usedEquipmentTypes.value.data = [];
    });

    return {
      equipmentTypes,
      loadEquipmentTypes,
      loadUsedEquipmentTypes,
      usedEquipmentTypes,
    };
  },
);

export const useEquipmentTypeStore = (): EquipmentTypeStore => {
  const store = _useEquipmentTypeStore();
  const { equipmentTypes, usedEquipmentTypes } = storeToRefs(store);
  const { loadEquipmentTypes, loadUsedEquipmentTypes } = store;

  return {
    equipmentTypes,
    loadEquipmentTypes,
    loadUsedEquipmentTypes,
    usedEquipmentTypes,
  };
};
